import React, { useState, useEffect } from 'react';
import {
    Code,
    Copy,
    Check,
    MessageCircle,
    Palette,
    Monitor,
    Zap,
    Smartphone,
    Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../lib/api';

const WidgetGenerator = () => {
    const { t } = useLanguage();
    const [copied, setCopied] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#2563eb');
    const [title, setTitle] = useState('Chat com IA');
    const [welcomeText, setWelcomeText] = useState('Olá! Como posso ajudar você hoje?');
    const [botId, setBotId] = useState('tpl_realestate_rental');
    const [bots, setBots] = useState([]);

    // Detect production or development URLs
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const widgetUrl = isProduction
        ? `https://${window.location.hostname}/widget.js`
        : `${window.location.origin}/widget.js`;
    const apiUrl = isProduction
        ? `https://api.${window.location.hostname.replace('www.', '')}`
        : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

    useEffect(() => {
        // Fetch user's bots
        const fetchBots = async () => {
            try {
                const response = await api.get('/api/bots');
                if (response.ok) {
                    const data = await response.json();
                    setBots(data.filter(b => !b.is_template));
                    if (data.length > 0) {
                        setBotId(data[0].bot_id);
                    }
                }
            } catch (err) {
                console.error('Error fetching bots:', err);
            }
        };
        fetchBots();
    }, []);

    const embedCode = `<!-- ChatAlfa Widget -->
<script src="${widgetUrl}"
  data-bot-id="${botId}"
  data-color="${primaryColor}"
  data-title="${title}"
  data-welcome="${welcomeText}"
  data-api-url="${apiUrl}"
  async>
</script>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(embedCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                        <Code className="h-8 w-8 text-blue-600" />
                        {t('widget.title')}
                    </h1>
                    <p className="text-slate-500 mt-1">{t('widget.subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="outline" className="gap-2">
                        <Monitor className="h-4 w-4" /> {t('widget.view')}
                    </Button>
                    <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 gap-2">
                        <Zap className="h-4 w-4" /> {t('common.publish')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Config Panel */}
                <div className="lg:col-span-5 space-y-6">
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Palette className="h-5 w-5 text-blue-600" /> {t('widget.customization')}
                            </CardTitle>
                            <CardDescription>{t('widget.customizationDesc')}</CardDescription>
                        </CardHeader>
                        <CardContent className="p-6 space-y-5">
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-700">{t('widget.activeBot')}</label>
                                <select
                                    className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                                    value={botId}
                                    onChange={(e) => setBotId(e.target.value)}
                                >
                                    {bots.length > 0 ? (
                                        bots.map(bot => (
                                            <option key={bot.bot_id} value={bot.bot_id}>{bot.name}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="tpl_realestate_rental">Imobiliária Pro</option>
                                            <option value="tpl_health_booking">Agendamento Médico</option>
                                            <option value="tpl_ecommerce_luxury">E-commerce Vendas</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-700">{t('widget.brandColor')}</label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={primaryColor}
                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                        className="h-10 w-10 rounded-lg cursor-pointer border-none p-0"
                                    />
                                    <Input
                                        value={primaryColor}
                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                        className="flex-1 font-mono uppercase"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-700">{t('widget.chatTitle')}</label>
                                <Input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t('widget.chatTitlePlaceholder')}
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-700">{t('widget.welcomeMessage')}</label>
                                <Input
                                    value={welcomeText}
                                    onChange={(e) => setWelcomeText(e.target.value)}
                                    placeholder={t('widget.welcomeMessagePlaceholder')}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-indigo-900 border-none text-white overflow-hidden shadow-xl shadow-indigo-100">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <Smartphone className="h-5 w-5 text-indigo-300" />
                                </div>
                                <h4 className="font-bold">{t('widget.mobileOptimized')}</h4>
                            </div>
                            <p className="text-indigo-200 text-sm leading-relaxed mb-6">{t('widget.mobileOptimizedDesc')}</p>
                            <Button className="w-full bg-white text-indigo-900 hover:bg-indigo-50 font-bold border-0">
                                {t('widget.testOnMobile')}
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Code and Preview */}
                <div className="lg:col-span-7 space-y-6">
                    <Card className="border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
                        <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Copy className="h-5 w-5 text-blue-600" /> {t('widget.installCode')}
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="text-blue-600 hover:text-blue-700 font-bold gap-2"
                                >
                                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    {copied ? t('common.copied') : t('common.copy')}
                                </Button>
                            </div>
                            <CardDescription>{t('widget.installCodeDesc')}</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0 bg-[#1e293b] flex-1 overflow-auto">
                            <pre className="p-6 text-sm font-mono text-indigo-200 leading-relaxed overflow-x-auto whitespace-pre-wrap">
                                {embedCode}
                            </pre>
                        </CardContent>
                    </Card>

                    {/* Live Preview Simulation */}
                    <Card className="border-slate-200 shadow-sm h-80 relative overflow-hidden bg-slate-100/50 border-dashed border-2">
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400 font-medium">
                            {t('widget.previewOnSite')}
                        </div>

                        {/* Floating Chat Bubble Simulation */}
                        <div className="absolute bottom-6 right-6 flex flex-col items-end gap-3 translate-x-0 transition-transform duration-500">
                            {/* Chat Window Mockup */}
                            <div className="w-72 bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 mb-2 scale-90 origin-bottom-right">
                                <div style={{ backgroundColor: primaryColor }} className="p-4 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                            <Sparkles className="h-4 w-4" />
                                        </div>
                                        <span className="font-bold text-sm">{title}</span>
                                    </div>
                                    <span className="text-xs opacity-60">{t('widget.online')}</span>
                                </div>
                                <div className="p-5 h-24 bg-slate-50">
                                    <div className="bg-white p-3 rounded-2xl rounded-tl-none text-xs text-slate-600 shadow-sm border border-slate-100 inline-block max-w-[90%]">
                                        {welcomeText}
                                    </div>
                                </div>
                                <div className="p-3 border-t border-slate-100 bg-white">
                                    <div className="h-8 bg-slate-100 rounded-full px-3 flex items-center text-[10px] text-slate-400">
                                        {t('widget.typePlaceholder')}
                                    </div>
                                </div>
                            </div>

                            {/* Final Bubble */}
                            <div
                                style={{ backgroundColor: primaryColor }}
                                className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-xl cursor-pointer hover:scale-110 transition-transform shadow-blue-500/20"
                            >
                                <MessageCircle className="h-6 w-6" />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default WidgetGenerator;
